import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Filter,
  Download,
  Plus,
  Edit3,
  Trash2,
  MoreVertical,
  Eye,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  Star,
  Check,
  AlertCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  ChevronsLeft,
  ChevronsRight,
  ZoomIn,
  Image as ImageIcon,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { IMAGE } from "../../api";
import { useOutletContext } from "react-router-dom";

// Fixed Input Dimensions - Consistent across all filters
const FILTER_INPUT_CONFIG = {
  width: "160px",
  height: "30px",
  fontSize: "11px",
  borderRadius: "6px",
};

// Image Modal Component - Fixed to work properly
const ImageModal = ({ isOpen, onClose, imageUrl, altText = "", darkMode }) => {
  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
      style={{ zIndex: 9999 }}
    >
      <div
        className={`relative max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 ${
          darkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            darkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h3
            className={`text-lg font-semibold ${
              darkMode ? "text-gray-200" : "text-gray-900"
            }`}
          >
            Image Preview
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors duration-200 hover:scale-105 ${
              darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
          >
            <X
              size={20}
              className={darkMode ? "text-gray-400" : "text-gray-600"}
            />
          </button>
        </div>

        {/* Image Container */}
        <div className="p-4">
          <img
            src={imageUrl}
            alt={altText}
            className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg mx-auto block"
            onError={(e) => {
              e.target.src =
                "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgMTMwQzExNi41NjkgMTMwIDEzMCAxMTYuNTY5IDEzMCAxMDBDMTMwIDgzLjQzMTUgMTE2LjU2OSA3MCAxMDAgNzBDODMuNDMxNSA3MCA3MCA4My40MzE1IDcwIDEwMEM3MCAxMTYuNTY5IDgzLjQzMTUgMTMwIDEwMCAxMzBaIiBmaWxsPSIjOUI5QjlCIi8+CjxwYXRoIGQ9Ik0xNjAgNTBIMTQwTDEzMCA0MEg3MEw2MCA1MEg0MEM0Ni4yNjA5IDUwIDUyLjM5MTMgNTIuNDg2NCA1Ni44OTM0IDU2Ljk4OTdDNjEuMzk1NyA2MS40OTMgNjQgNjcuNjA4NyA2NCA3NEgxMzZDMTM2IDY3LjYwODcgMTM4LjYwNCA2MS40OTMgMTQzLjEwNyA1Ni45ODk3QzE0Ny42MDkgNTIuNDg2NCAxNTMuNzM5IDUwIDE2MCA1MFoiIGZpbGw9IiM5QjlCOUIiLz4KPC9zdmc+"; // Fallback placeholder
            }}
            style={{ maxWidth: "100%", maxHeight: "70vh" }}
          />
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-center p-4 border-t ${
            darkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <span
            className={`text-sm ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Click outside or press ESC to close
          </span>
        </div>
      </div>
    </div>
  );
};

const UltimateDataTable = ({
  title = "Ultimate Data Table",
  subtitle = "Manage your data with style",
  columns = [],
  loadApi = null,
  staticData = [],
  serverSide = true,
  apiParams = {},
  exportApi = null,
  onAdd,
  onEdit,
  save,
  onView,
  onDelete,
  onBulkAction,
  className = "",
  height = "calc(100vh - 210px)",
  hideAction = false,
  hideCheckBox= false,
  // darkMode
}) => {
  // Dark Mode Check
  // const darkMode = JSON.parse(sessionStorage.getItem("darkMode")) || false;

  const { darkMode, setDarkMode } = useOutletContext();

  // State Management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});
  const [rowSelection, setRowSelection] = useState({});

  // console.log(rowSelection , "rowSelection")
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 12,
  });
  const [totalCount, setTotalCount] = useState(0);

  // Image Modal State
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    altText: "",
  });

  // Debounced filters - only for server-side
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const debounceRef = useRef(null);

  // Debounce filter changes only for server-side
  useEffect(() => {
    if (serverSide) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(
        () => setDebouncedFilters(columnFilters),
        400
      );
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    } else {
      // For client-side, use filters immediately
      setDebouncedFilters(columnFilters);
    }
  }, [columnFilters, serverSide]);

  // Client-side filtered data - Fixed filtering logic
  const filteredData = useMemo(() => {
    if (serverSide) return data;

    let filtered = [...staticData];

    // Apply column filters
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (value && String(value).trim() !== "") {
        filtered = filtered.filter((row) => {
          const cellValue = row[key];
          if (cellValue == null) return false;

          // Handle different filter types
          const column = columns.find((col) => col.accessorKey === key);
          if (column?.filterType === "select") {
            return (
              String(cellValue).toLowerCase() === String(value).toLowerCase()
            );
          } else if (
            column?.filterType === "date" ||
            column?.filterType === "datetime"
          ) {
            if (String(value).includes(",")) {
              // Date range
              const [start, end] = String(value).split(",");
              const rowDate = new Date(cellValue);
              return rowDate >= new Date(start) && rowDate <= new Date(end);
            } else {
              // Single date
              const rowDate = new Date(cellValue);
              const filterDate = new Date(value);
              return rowDate.toDateString() === filterDate.toDateString();
            }
          } else {
            // Text search - case insensitive
            return String(cellValue)
              .toLowerCase()
              .includes(String(value).toLowerCase());
          }
        });
      }
    });

    return filtered;
  }, [serverSide, staticData, columnFilters, columns]);

  // Stable column configuration to prevent re-renders
  const stableColumns = useMemo(() => columns, [JSON.stringify(columns)]);

  // Custom Action Buttons Component
  const ActionButtons = ({ row, onEdit, onView, onDelete, darkMode }) => {
    return (
      <div className="flex justify-center items-center gap-1">
        {onView && (
          <button
            onClick={() => onView(row.original)}
            className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 ${
              darkMode
                ? "text-blue-400 hover:bg-blue-900/20"
                : "text-blue-600 hover:bg-blue-50"
            }`}
            title="View"
          >
            <Eye size={16} />
          </button>
        )}

        {onEdit && (
          <button
            onClick={() => onEdit(row.original)}
            className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 ${
              darkMode
                ? "text-emerald-400 hover:bg-emerald-900/20"
                : "text-emerald-600 hover:bg-emerald-50"
            }`}
            title="Edit"
          >
            <Edit3 size={16} />
          </button>
        )}

        {onDelete && (
          <button
            onClick={() => onDelete(row.original)}
            className={`p-2 rounded-lg transition-all duration-200 hover:scale-105 ${
              darkMode
                ? "text-red-400 hover:bg-red-900/20"
                : "text-red-600 hover:bg-red-50"
            }`}
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    );
  };

  // Enhanced Column Configuration - Fixed to prevent re-renders
  const enhancedColumns = useMemo(() => {
    const baseColumns = [
      // Selection Column

      !hideCheckBox && {
        id: "select",
        size: 50,
        header: ({ table }) => (
          <div className="flex justify-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
              />
              <div
                className={`w-5 h-5 border-2 rounded peer-hover:border-blue-400 transition-all duration-200 flex items-center justify-center ${
                  darkMode
                    ? "bg-gray-800 border-gray-600 peer-checked:bg-blue-600 peer-checked:border-blue-600"
                    : "bg-white border-gray-300 peer-checked:bg-blue-600 peer-checked:border-blue-600"
                }`}
              >
                {table.getIsAllRowsSelected() && (
                  <Check size={12} className="text-white" />
                )}
              </div>
            </label>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
              <div
                className={`w-5 h-5 border-2 rounded peer-hover:border-blue-400 transition-all duration-200 flex items-center justify-center ${
                  darkMode
                    ? "bg-gray-800 border-gray-600 peer-checked:bg-blue-600 peer-checked:border-blue-600"
                    : "bg-white border-gray-300 peer-checked:bg-blue-600 peer-checked:border-blue-600"
                }`}
              >
                {row.getIsSelected() && (
                  <Check size={12} className="text-white" />
                )}
              </div>
            </label>
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },

      // User Columns with Enhanced Filters - Stable references
      ...stableColumns.map((col, index) => ({
        ...col,
        id: col.id || col.accessorKey || `column-${index}`,
        header: ({ column }) => (
          <ColumnHeader column={column} col={col} darkMode={darkMode} />
        ),
        cell: ({ getValue, row }) => {
          const value = getValue();
          return (
            <CellRenderer
              column={col}
              value={value}
              row={row}
              darkMode={darkMode}
              onImageClick={(imageUrl, altText) => {
                console.log("Opening image modal:", imageUrl); // Debug log
                setImageModal({
                  isOpen: true,
                  imageUrl,
                  altText,
                });
              }}
            />
          );
        },
      })),

      // Actions Column - Updated to use your ActionButtons
      !hideAction && {
        id: "actions",
        size: 120,
        header: () => (
          <div className="text-center">
            <h3
              className={`font-semibold text-sm ${
                darkMode ? "text-gray-200" : "text-gray-900"
              }`}
            >
              Actions
            </h3>
          </div>
        ),
        cell: ({ row }) => (
          <ActionButtons
            row={row}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            darkMode={darkMode}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ].filter((e)=>e);

    return baseColumns;
  }, [stableColumns, onEdit, onView, onDelete, darkMode]);

  // Separate Column Header Component to prevent re-renders
  const ColumnHeader = ({ column, col, darkMode }) => {
    const filterValue = columnFilters[col.accessorKey] || "";

    return (
      <div className="space-y-1 min-w-[140px]">
        {/* Column Title & Sort */}
        <div className="flex items-center justify-between">
          <h3
            className={`font-semibold text-sm tracking-tight ${
              darkMode ? "text-gray-200" : "text-gray-900"
            }`}
          >
            {col.header}
          </h3>
          {col.enableSorting !== false && (
            <button
              onClick={column.getToggleSortingHandler()}
              className={`p-1.5 rounded-lg transition-colors duration-200 group ${
                darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
              }`}
            >
              {column.getIsSorted() === "asc" ? (
                <ChevronUp size={16} className="text-blue-500" />
              ) : column.getIsSorted() === "desc" ? (
                <ChevronDown size={16} className="text-blue-500" />
              ) : (
                <ChevronsUpDown
                  size={16}
                  className={`group-hover:${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  } ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                />
              )}
            </button>
          )}
        </div>

        {/* Fixed Size Filter Input */}
        {col.filterable && (
          <FilterInput
            key={`filter-${col.accessorKey}`}
            column={col}
            value={filterValue}
            onChange={(value) => {
              console.log("Filter changed:", col.accessorKey, value); // Debug log
              setColumnFilters((prev) => ({
                ...prev,
                [col.accessorKey]: value,
              }));
            }}
            darkMode={darkMode}
          />
        )}
      </div>
    );
  };

  // Fixed Filter Input Component - Separate component to prevent re-renders
  const FilterInput = ({ column, value, onChange, darkMode }) => {
    const [localValue, setLocalValue] = useState(value);

    // Sync with parent value
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleInputChange = (newValue) => {
      setLocalValue(newValue);
      onChange(newValue);
    };

    const handleClear = () => {
      setLocalValue("");
      onChange("");
    };

    const baseInputStyles = {
      width: FILTER_INPUT_CONFIG.width,
      height: FILTER_INPUT_CONFIG.height,
      fontSize: FILTER_INPUT_CONFIG.fontSize,
      borderRadius: FILTER_INPUT_CONFIG.borderRadius,
    };

    const inputClasses = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-200 ${
      darkMode
        ? "bg-gray-800 border-gray-600 text-gray-200 placeholder-gray-400 hover:border-gray-500 focus:bg-gray-700"
        : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300 focus:bg-white"
    }`;

    switch (column.filterType) {
      case "select":
        return (
          <div className="relative">
            <select
              value={localValue}
              onChange={(e) => handleInputChange(e.target.value)}
              style={baseInputStyles}
              className={`${inputClasses} appearance-none pr-8 cursor-pointer`}
            >
              <option value="">All {column.header}</option>
              {column.filterOptions?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className={`absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                darkMode ? "text-gray-400" : "text-gray-400"
              }`}
            />
          </div>
        );

      case "date":
        return (
          <div className="relative">
            <DatePicker
              selected={localValue ? new Date(localValue) : null}
              onChange={(date) =>
                handleInputChange(date ? date.toISOString().split("T")[0] : "")
              }
              customInput={
                <input
                  style={baseInputStyles}
                  className={`${inputClasses} pl-9 cursor-pointer`}
                  placeholder="Select date"
                  readOnly
                />
              }
              popperClassName="react-datepicker-popper"
              calendarClassName={`react-datepicker-calendar ${
                darkMode ? "dark-calendar" : ""
              }`}
            />
            <Calendar
              size={16}
              className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                darkMode ? "text-gray-400" : "text-gray-400"
              }`}
            />
            {localValue && (
              <button
                type="button"
                onClick={handleClear}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors duration-200 ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                <X
                  size={14}
                  className={darkMode ? "text-gray-400" : "text-gray-400"}
                />
              </button>
            )}
          </div>
        );

      case "datetime":
        return (
          <div className="relative">
            <DatePicker
              selected={localValue ? new Date(localValue) : null}
              onChange={(date) =>
                handleInputChange(date ? date.toISOString() : "")
              }
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MM/dd/yyyy HH:mm"
              customInput={
                <input
                  style={baseInputStyles}
                  className={`${inputClasses} pl-9 cursor-pointer`}
                  placeholder="Select date & time"
                  readOnly
                />
              }
            />
            <Clock
              size={16}
              className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                darkMode ? "text-gray-400" : "text-gray-400"
              }`}
            />
            {localValue && (
              <button
                type="button"
                onClick={handleClear}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors duration-200 ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                <X
                  size={14}
                  className={darkMode ? "text-gray-400" : "text-gray-400"}
                />
              </button>
            )}
          </div>
        );

      case "dateRange":
        // Parse start and end dates from comma-separated value
        const [startDateStr, endDateStr] = localValue ? localValue.split(",") : ["", ""];
        const startDate = startDateStr ? new Date(startDateStr) : null;
        const endDate = endDateStr ? new Date(endDateStr) : null;

        const handleStartDateChange = (date) => {
          const newStartDate = date ? date.toISOString().split("T")[0] : "";
          const newValue = newStartDate && endDateStr ? `${newStartDate},${endDateStr}` : newStartDate;
          handleInputChange(newValue);
        };

        const handleEndDateChange = (date) => {
          const newEndDate = date ? date.toISOString().split("T")[0] : "";
          const newValue = startDateStr && newEndDate ? `${startDateStr},${newEndDate}` : newEndDate;
          handleInputChange(newValue);
        };

        return (
          <div className="flex gap-1 items-center flex-wrap">
            {/* Start Date */}
            <div className="relative flex-shrink-0">
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                maxDate={endDate}
                customInput={
                  <input
                    style={baseInputStyles}
                    className={`${inputClasses} pl-9 cursor-pointer`}
                    placeholder="From"
                    readOnly
                  />
                }
                popperClassName="react-datepicker-popper"
                calendarClassName={`react-datepicker-calendar ${
                  darkMode ? "dark-calendar" : ""
                }`}
              />
              <Calendar
                size={16}
                className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                  darkMode ? "text-gray-400" : "text-gray-400"
                }`}
              />
            </div>

            {/* Separator */}
            <span className={`text-xs font-semibold flex-shrink-0 ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}>
              to
            </span>

            {/* End Date */}
            <div className="relative flex-shrink-0">
              <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                customInput={
                  <input
                    style={baseInputStyles}
                    className={`${inputClasses} pl-9 cursor-pointer`}
                    placeholder="To"
                    readOnly
                  />
                }
                popperClassName="react-datepicker-popper"
                calendarClassName={`react-datepicker-calendar ${
                  darkMode ? "dark-calendar" : ""
                }`}
              />
              <Calendar
                size={16}
                className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                  darkMode ? "text-gray-400" : "text-gray-400"
                }`}
              />
            </div>

            {/* Clear Button */}
            {localValue && (
              <button
                type="button"
                onClick={handleClear}
                className={`p-1 rounded-full transition-colors duration-200 flex-shrink-0 ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                <X
                  size={14}
                  className={darkMode ? "text-gray-400" : "text-gray-400"}
                />
              </button>
            )}
          </div>
        );

      default:
        return (
          <div className="relative">
            <input
              type="text"
              value={localValue}
              onChange={(e) => handleInputChange(e.target.value)}
              style={baseInputStyles}
              className={`${inputClasses} pl-9`}
              placeholder={`Search ${column.header}`}
              autoComplete="off"
            />
            <Search
              size={16}
              className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                darkMode ? "text-gray-400" : "text-gray-400"
              }`}
            />
            {localValue && (
              <button
                type="button"
                onClick={handleClear}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors duration-200 ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                <X
                  size={14}
                  className={darkMode ? "text-gray-400" : "text-gray-400"}
                />
              </button>
            )}
          </div>
        );
    }
  };

  // Enhanced Cell Renderer - Fixed image click handler
  const CellRenderer = ({ column, value, row, darkMode, onImageClick }) => {
    // console.log(IMAGE + value,"dsfdsfdsfds" )
    switch (column.cellType) {
      case "image":
        return (
          <div className="flex justify-center">
            {value ? (
              <div
                className="relative group  cursor-pointer"
                onClick={() => {
                  console.log("Image clicked:", value);
                  onImageClick(value, column.header || "Image");
                }}
              >
                <img
                  src={IMAGE + value}
                  alt={column.header || "Image"}
                  className={`w-12 h-12 object-cover rounded-lg cursor-pointer transition-all duration-200 border-2 hover:scale-105 ${
                    darkMode
                      ? "border-gray-600 hover:border-blue-500"
                      : "border-gray-200 hover:border-blue-400"
                  }`}
                  onClick={() => {
                    console.log("Image clicked:", value); // Debug log
                    onImageClick(value, column.header || "Image");
                  }}
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgMTMwQzExNi41NjkgMTMwIDEzMCAxMTYuNTY5IDEzMCAxMDBDMTMwIDgzLjQzMTUgMTE2LjU2OSA3MCAxMDAgNzBDODMuNDMxNSA3MCA3MCA4My40MzE1IDcwIDEwMEM3MCAxMTYuNTY5IDgzLjQzMTUgMTMwIDEwMCAxMzBaIiBmaWxsPSIjOUI5QjlCIi8+CjxwYXRoIGQ9Ik0xNjAgNTBIMTQwTDEzMCA0MEg3MEw2MCA1MEg0MEM0Ni4yNjA5IDUwIDUyLjM5MTMgNTIuNDg2NCA1Ni44OTM0IDU2Ljk4OTdDNjEuMzk1NyA2MS40OTMgNjQgNjcuNjA4NyA2NCA3NEgxMzZDMTM2IDY3LjYwODcgMTM4LjYwNCA2MS40OTMgMTQzLjEwNyA1Ni45ODk3QzE0Ny42MDkgNTIuNDg2NCAxNTMuNzM5IDUwIDE2MCA1MFoiIGZpbGw9IiM5QjlCOUIiLz4KPC9zdmc+";
                  }}
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                  <ZoomIn size={16} className="text-white" />
                </div>
              </div>
            ) : (
              <div
                className={`w-12 h-12 rounded-lg border-2 border-dashed flex items-center justify-center ${
                  darkMode
                    ? "border-gray-600 bg-gray-800"
                    : "border-gray-300 bg-gray-100"
                }`}
              >
                <ImageIcon
                  size={16}
                  className={darkMode ? "text-gray-500" : "text-gray-400"}
                />
              </div>
            )}
          </div>
        );

      case "custom":
        if (typeof column.customRender === "function") {
          return (
            <div className={darkMode ? "text-gray-200" : "text-gray-900"}>
              {column.customRender(value, row.original, darkMode)}
            </div>
          );
        }
        return (
          <span
            className={`text-sm ${
              darkMode ? "text-gray-200" : "text-gray-900"
            }`}
          >
            {value || "-"}
          </span>
        );

      case "cast":
        return (
          <div className="space-y-1">
            <p
              className={`text-sm font-medium ${
                darkMode ? "text-gray-200" : "text-gray-900"
              }`}
            >
              {row.original.name || value}
            </p>
            <p
              className={`text-xs ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {row.original.cast_type || "Actor"}
            </p>
          </div>
        );

      case "user":
        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {value ? (
                <img
                  src={value}
                  alt=""
                  className={`w-10 h-10 rounded-full object-cover ring-2 ${
                    darkMode ? "ring-gray-600" : "ring-gray-100"
                  }`}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <User size={20} className="text-white" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={`text-sm font-medium truncate ${
                  darkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                {row.original.name}
              </p>
              <p
                className={`text-sm truncate ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {row.original.email}
              </p>
            </div>
          </div>
        );

      case "status":
        const statusConfig = {
          Active: {
            bg: darkMode ? "bg-emerald-900/30" : "bg-emerald-50",
            text: darkMode ? "text-emerald-300" : "text-emerald-700",
            dot: "bg-emerald-500",
          },
          Inactive: {
            bg: darkMode ? "bg-red-900/30" : "bg-red-50",
            text: darkMode ? "text-red-300" : "text-red-700",
            dot: "bg-red-500",
          },
          Pending: {
            bg: darkMode ? "bg-amber-900/30" : "bg-amber-50",
            text: darkMode ? "text-amber-300" : "text-amber-700",
            dot: "bg-amber-500",
          },
          Draft: {
            bg: darkMode ? "bg-gray-800" : "bg-gray-50",
            text: darkMode ? "text-gray-300" : "text-gray-700",
            dot: darkMode ? "bg-gray-500" : "bg-gray-500",
          },
        };
        const config = statusConfig[value] || statusConfig.Draft;

        return (
          <span
            className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${config.dot} mr-1.5`}
            ></span>
            {value}
          </span>
        );

      case "date":
        return (
          <div
            className={`flex items-center space-x-2 text-sm ${
              darkMode ? "text-gray-200" : "text-gray-900"
            }`}
          >
            <Calendar
              size={16}
              className={darkMode ? "text-gray-400" : "text-gray-400"}
            />
            <span>{value ? new Date(value).toLocaleDateString("en-US", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }) : "-"}</span>
          </div>
        );

      case "currency":
        return (
          <div className="text-right">
            <span
              className={`text-sm font-semibold ${
                darkMode ? "text-gray-200" : "text-gray-900"
              }`}
            >
              $
              {parseFloat(value || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        );

      case "rating":
        const rating = Math.floor(value || 0);
        return (
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={16}
                className={
                  star <= rating
                    ? "text-yellow-400 fill-current"
                    : darkMode
                    ? "text-gray-600"
                    : "text-gray-300"
                }
              />
            ))}
            <span
              className={`text-xs ml-1 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              ({value || 0})
            </span>
          </div>
        );

      default:
        return (
          <span
            className={`text-sm ${
              darkMode ? "text-gray-200" : "text-gray-900"
            }`}
          >
            {value || "-"}
          </span>
        );
    }
  };

  // Helper function to transform dateRange filters
  const transformFiltersForAPI = (filters) => {
    const transformed = { ...filters };
    
    // Find all columns with dateRange filter type
    columns.forEach((col) => {
      const key = col.accessorKey;
      if (col.filterType === "dateRange" && transformed[key]) {
        const [startDate, endDate] = String(transformed[key]).split(",");
        delete transformed[key]; // Remove the original comma-separated value
        
        // Add separate startDate and endDate keys
        if (startDate) transformed[`${key}_start`] = startDate;
        if (endDate) transformed[`${key}_end`] = endDate;
      }
    });
    
    return transformed;
  };

  // Data Fetching Logic - Fixed for proper client-side filtering
  const fetchData = useCallback(async () => {
    if (serverSide && loadApi) {
      setLoading(true);
      setError(null);

      try {
        const params = {
          page_number: pagination.pageIndex + 1,
          row_size: pagination.pageSize,
          sort_field: sorting[0]?.id,
          sort_direction: sorting[0]?.desc ? "desc" : "asc",
          ...transformFiltersForAPI(debouncedFilters),
          ...apiParams,
        };

        const response = await loadApi(params);

        if (response.success) {
          setData(response.data || []);
          setTotalCount(response.totalCount || 0);
        } else {
          setError(response.message || "Failed to fetch data");
        }
      } catch (err) {
        setError(err.message || "An error occurred while fetching data");
      } finally {
        setLoading(false);
      }
    } else {
      // Client-side mode - just set the static data
      setData(staticData);
      setTotalCount(staticData.length);
    }
  }, [
    serverSide,
    loadApi,
    pagination,
    sorting,
    debouncedFilters,
    apiParams,
    staticData,
    save,
  ]);

  useEffect(() => {
    fetchData();
  }, [
    // serverSide,
    // loadApi,
    pagination,
    sorting,
    debouncedFilters,
    save,
    // apiParams,
    // staticData,
  ]);

  // Table Configuration - Fixed for client-side filtering
  const table = useReactTable({
    data: serverSide ? data : filteredData, // Use filteredData for client-side
    columns: enhancedColumns,
    manualPagination: serverSide,
    manualSorting: serverSide,
    manualFiltering: serverSide,
    pageCount: serverSide
      ? Math.ceil(totalCount / pagination.pageSize)
      : Math.ceil(filteredData.length / pagination.pageSize),
    state: {
      sorting,
      rowSelection,
      pagination,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    getRowCanSelect: (row) => row.index !== 0, // disables checkbox for first row
  });

  // Export Function
  const handleExport = async () => {
    if (serverSide && exportApi) {
      try {
        const params = {
          sort_field: sorting[0]?.id,
          sort_direction: sorting[0]?.desc ? "desc" : "asc",
          ...debouncedFilters,
          ...apiParams,
          export: true,
        };

        const response = await exportApi(params);
        if (response instanceof Blob) {
          saveAs(
            response,
            `${title}_export_${new Date().toISOString().split("T")[0]}.xlsx`
          );
        }
      } catch (error) {
        console.error("Export failed:", error);
        setError("Export failed. Please try again.");
      }
    } else {
      // Export filtered data for client-side
      const exportData = filteredData;
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      XLSX.writeFile(
        wb,
        `${title}_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );
    }
  };

  const selectedRows = Object.keys(rowSelection).length;

  return (
    <>
      {/* Image Modal - Fixed positioning and z-index */}
      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => {
          console.log("Closing image modal"); // Debug log
          setImageModal({ isOpen: false, imageUrl: "", altText: "" });
        }}
        imageUrl={IMAGE + imageModal.imageUrl}
        altText={imageModal.altText}
        darkMode={darkMode}
      />

      {/* Main Table Component */}
      <div
        className={`rounded-2xl shadow-xl border overflow-hidden ${className} ${
          darkMode
            ? "bg-gray-900/95 border-gray-700/50"
            : "bg-white/95 border-gray-100/50"
        } backdrop-blur-lg`}
      >
        {/* Premium Header */}
        <div
          className={`px-5 py-3 border-b ${
            darkMode
              ? "bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 border-gray-700/50"
              : "bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 border-gray-100"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1
                className={`text-lg font-bold bg-gradient-to-r bg-clip-text text-transparent ${
                  darkMode
                    ? "from-gray-100 to-gray-300"
                    : "from-gray-900 to-gray-700"
                }`}
              >
                {title}
              </h1>
              <p
                className={`text-xs ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {subtitle}
              </p>
              <div className="flex items-center space-x-2 mt-1">
                <span
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {(serverSide
                    ? totalCount
                    : filteredData.length
                  ).toLocaleString()}{" "}
                  total items
                </span>
                {selectedRows > 0 && (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span
                      className={`text-sm font-medium ${
                        darkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    >
                      {selectedRows} selected
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleExport}
                className={`px-3 py-1.5 text-sm text-white rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                  darkMode
                    ? "bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500"
                    : "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-600"
                }`}
              >
                <Download size={18} />
                <span>Export</span>
              </button>

              {onAdd && (
                <button
                  onClick={onAdd}
                  className={`px-3 py-1.5 text-sm text-white rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                    darkMode
                      ? "bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-600 hover:to-indigo-600"
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  }`}
                >
                  <Plus size={18} />
                  <span>Add New</span>
                </button>
              )}
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedRows > 0 && (
            <div
              className={`mt-4 p-4 border rounded-xl ${
                darkMode
                  ? "bg-blue-900/20 border-blue-800/50"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`font-medium ${
                    darkMode ? "text-blue-400" : "text-blue-800"
                  }`}
                >
                  {selectedRows} item(s) selected
                </span>
                <div className="flex space-x-2">
                  {/* <button
                    onClick={() =>
                      onBulkAction?.("delete", Object.keys(rowSelection))
                    }
                    className={`px-3 py-1.5 text-white rounded-lg font-medium transition-colors duration-200 text-sm flex items-center space-x-1 ${
                      darkMode
                        ? "bg-red-700 hover:bg-red-600"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </button> */}
                  <button
                    onClick={() => {
                      const selectedRowsData = table
                        .getSelectedRowModel()
                        .flatRows.map((row) => row.original);

                      onBulkAction?.("delete", selectedRowsData);
                    }}
                    className={`px-3 py-1.5 text-white rounded-lg font-medium transition-colors duration-200 text-sm flex items-center space-x-1 ${
                      darkMode
                        ? "bg-red-700 hover:bg-red-600"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </button>
                  <button
                    onClick={() =>
                      onBulkAction?.("export", Object.keys(rowSelection))
                    }
                    className={`px-3 py-1.5 text-white rounded-lg font-medium transition-colors duration-200 text-sm flex items-center space-x-1 ${
                      darkMode
                        ? "bg-emerald-700 hover:bg-emerald-600"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                  >
                    <Download size={14} />
                    <span>Export</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div
            className={`mx-8 mt-4 p-4 border rounded-xl flex items-center space-x-3 ${
              darkMode
                ? "bg-red-900/20 border-red-800/50"
                : "bg-red-50 border-red-200"
            }`}
          >
            <AlertCircle size={20} className="text-red-500" />
            <span className={darkMode ? "text-red-400" : "text-red-800"}>
              {error}
            </span>
            <button
              onClick={() => setError(null)}
              className={`ml-auto p-1 rounded-full transition-colors duration-200 ${
                darkMode ? "hover:bg-red-800/30" : "hover:bg-red-100"
              }`}
            >
              <X size={16} className="text-red-500" />
            </button>
          </div>
        )}

        {/* Premium Table */}
        <div style={{ height }} className="overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto h-full">
            <table className="w-full">
              {/* Table Header */}
              <thead
                className={`sticky top-0 z-10 ${
                  darkMode
                    ? "bg-gradient-to-r from-gray-800 to-gray-700"
                    : "bg-gradient-to-r from-gray-50 to-gray-100"
                }`}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className={`border-b ${
                      darkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-3 py-2 text-left border-r last:border-r-0 backdrop-blur-sm ${
                          darkMode
                            ? "border-gray-700 bg-gray-800/80"
                            : "border-gray-100 bg-white/80"
                        }`}
                        style={{
                          width: header.getSize(),
                          minWidth: header.column.columnDef.minWidth || "auto",
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              {/* Table Body */}
              <tbody
                className={`divide-y ${
                  darkMode ? "divide-gray-700" : "divide-gray-100"
                }`}
              >
                {loading ? (
                  Array.from({ length: pagination.pageSize }).map(
                    (_, index) => (
                      <tr
                        key={index}
                        className={
                          darkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                        }
                      >
                        {enhancedColumns.map((_, colIndex) => (
                          <td key={colIndex} className="px-3 py-2">
                            <div className="flex items-center space-x-3">
                              <div
                                className={`w-8 h-8 rounded-full animate-pulse ${
                                  darkMode ? "bg-gray-700" : "bg-gray-200"
                                }`}
                              ></div>
                              <div className="flex-1 space-y-2">
                                <div
                                  className={`h-4 rounded animate-pulse ${
                                    darkMode ? "bg-gray-700" : "bg-gray-200"
                                  }`}
                                ></div>
                                <div
                                  className={`h-3 rounded w-2/3 animate-pulse ${
                                    darkMode ? "bg-gray-700" : "bg-gray-200"
                                  }`}
                                ></div>
                              </div>
                            </div>
                          </td>
                        ))}
                      </tr>
                    )
                  )
                ) : (serverSide ? data : filteredData).length === 0 ? (
                  <tr>
                    <td colSpan={enhancedColumns.length} className="px-6 py-16">
                      <div className="text-center">
                        <div
                          className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                            darkMode
                              ? "bg-gradient-to-br from-gray-800 to-gray-700"
                              : "bg-gradient-to-br from-gray-100 to-gray-200"
                          }`}
                        >
                          <Search
                            size={24}
                            className={
                              darkMode ? "text-gray-400" : "text-gray-400"
                            }
                          />
                        </div>
                        <h3
                          className={`text-lg font-medium mb-2 ${
                            darkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          No data found
                        </h3>
                        <p
                          className={`mb-6 ${
                            darkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {Object.keys(columnFilters).length > 0
                            ? "Try adjusting your filters to see more results"
                            : "Get started by adding some data"}
                        </p>
                        {onAdd && Object.keys(columnFilters).length === 0 && (
                          <button
                            onClick={onAdd}
                            className={`px-6 py-3 text-white rounded-xl font-medium transition-colors duration-200 flex items-center space-x-2 mx-auto ${
                              darkMode
                                ? "bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-600 hover:to-indigo-600"
                                : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                            }`}
                          >
                            <Plus size={20} />
                            <span>Add First Item</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, index) => (
                    <tr
                      key={row.id}
                      className={`transition-all duration-200 ${
                        row.getIsSelected()
                          ? darkMode
                            ? "bg-blue-900/20 border-blue-800"
                            : "bg-blue-50 border-blue-200"
                          : darkMode
                          ? "hover:bg-gray-800/50"
                          : "hover:bg-gray-50"
                      } ${
                        index % 2 === 0
                          ? darkMode
                            ? "bg-gray-800/30"
                            : "bg-gray-50/30"
                          : darkMode
                          ? "bg-gray-900"
                          : "bg-white"
                      }`}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`px-3 py-2 border-r last:border-r-0 ${
                            darkMode ? "border-gray-700" : "border-gray-100"
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Premium Pagination */}
        <div
          className={`px-5 py-2 border-t ${
            darkMode
              ? "bg-gradient-to-r from-gray-800 to-gray-700 border-gray-700"
              : "bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Rows per page:
              </span>
              <select
                value={pagination.pageSize}
                onChange={(e) =>
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                    pageIndex: 0,
                  }))
                }
                className={`px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                  darkMode
                    ? "bg-gray-800 border-gray-600 text-gray-200"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
              >
                {[12, 24, 48, 96].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <span
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {Math.min(
                  pagination.pageIndex * pagination.pageSize + 1,
                  serverSide ? totalCount : filteredData.length
                )}{" "}
                -{" "}
                {Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  serverSide ? totalCount : filteredData.length
                )}{" "}
                of{" "}
                {(serverSide
                  ? totalCount
                  : filteredData.length
                ).toLocaleString()}
              </span>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
                  }
                  disabled={!table.getCanPreviousPage()}
                  className={`p-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode ? "hover:bg-gray-700" : "hover:bg-white"
                  }`}
                >
                  <ChevronsLeft
                    size={18}
                    className={darkMode ? "text-gray-400" : "text-gray-600"}
                  />
                </button>
                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: prev.pageIndex - 1,
                    }))
                  }
                  disabled={!table.getCanPreviousPage()}
                  className={`p-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode ? "hover:bg-gray-700" : "hover:bg-white"
                  }`}
                >
                  <ArrowLeft
                    size={18}
                    className={darkMode ? "text-gray-400" : "text-gray-600"}
                  />
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from(
                    { length: Math.min(5, table.getPageCount()) },
                    (_, i) => {
                      const pageIndex = pagination.pageIndex;
                      const totalPages = table.getPageCount();
                      let pageNum;

                      if (totalPages <= 5) {
                        pageNum = i;
                      } else if (pageIndex < 3) {
                        pageNum = i;
                      } else if (pageIndex >= totalPages - 3) {
                        pageNum = totalPages - 5 + i;
                      } else {
                        pageNum = pageIndex - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() =>
                            setPagination((prev) => ({
                              ...prev,
                              pageIndex: pageNum,
                            }))
                          }
                          className={`w-10 h-10 rounded-lg font-medium transition-all duration-200 ${
                            pageNum === pagination.pageIndex
                              ? darkMode
                                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25"
                                : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                              : darkMode
                              ? "bg-gray-800/80 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700/50 hover:border-gray-600"
                              : "bg-gray-50/80 text-gray-700 hover:bg-gray-100 hover:text-gray-900 border border-gray-200/50 hover:border-gray-300"
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: prev.pageIndex + 1,
                    }))
                  }
                  disabled={!table.getCanNextPage()}
                  className={`p-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode ? "hover:bg-gray-700" : "hover:bg-white"
                  }`}
                >
                  <ArrowRight
                    size={18}
                    className={darkMode ? "text-gray-400" : "text-gray-600"}
                  />
                </button>
                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: table.getPageCount() - 1,
                    }))
                  }
                  disabled={!table.getCanNextPage()}
                  className={`p-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode ? "hover:bg-gray-700" : "hover:bg-white"
                  }`}
                >
                  <ChevronsRight
                    size={18}
                    className={darkMode ? "text-gray-400" : "text-gray-600"}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UltimateDataTable;
